import _ from "lodash";
import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import classes from "./ToolResponse.scss";
import { connect } from "react-redux";
import AssessmentGradeScaleTool from "AppComponents/AssessmentGradeScaleTool";
import EvaluationStatus from "../EvaluationStatus";
import {
  getUpdatedToolResponses,
  handleAssessmentToolResponse,
  handleAssessmentToolResponseInCache,
} from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { updateField } from "modules/FieldUpdateThunks";
import RubricTable from "Rubric/components/RubricTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import ChecklistTable from "Checklist/components/ChecklistTable";
import MYPObjectiveRubric from "UnitPlans/components/IBcomponents/AssessmentTool/MYPObjectiveRubric";
import {
  TextAreaInput,
  IconButton,
  Dropdown,
  Tooltip,
} from "@toddle-edu/ds-web";
import { StarsTwoFilled, TickCircleOpenOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import CriteriaRubricAssessmentWrapper from "UnitPlans/components/IBcomponents/CriteriaRubricAssessmentWrapper";
import ScoreAssessmentTool from "AppComponents/ScoreAssessmentTool";
import StandardRubric from "AppComponents/StandardRubric";
import AIGradingPromptBox from "AppComponents/AIGradingPromptBox";
import AIGradingActions from "AppComponents/AIGradingActions";
import AssessmentIBScaleTool from "UnitPlans/components/IBcomponents/AssessmentIBScaleTool";
import AutomaticIBScaleTool from "UnitPlans/components/IBcomponents/AssessmentIBScaleTool/AutomaticIBScaleTool";
import AutomaticIBScaleToolV2 from "UnitPlans/components/IBcomponents/AssessmentIBScaleTool/AutomaticIBScaleToolV2";
import PypEvaluationV2 from "AssessmentEvaluation/components/PypEvaluationV2";
import {
  STAFF_USER,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  GRADE_SCALE_TOOL,
  CURRICULUM_TYPE_MYP,
  MYP_IB_DEFINED,
} from "Constants/stringConstants";
import { fontStyle } from "Constants";
import {
  handleIBPYPElementRatingsForStandardRubric,
  handleIBPYPElementRatingsForGenericRubric,
  updateEvaluationData,
  updateScoreAssessmentTool,
} from "ClassRoom/modules/ClassRoomModule";
import { getIsAIGeneratingResponseForTool } from "ClassRoom/modules/ClassRoomSelectors";
import { getIsIBGradeScaleV2ForNewGradingSetupEnabled } from "Utils/ACLStoreUtils";
import { compose, graphql } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import OverrideStandardRubricScore from "AppComponents/StandardRubric/components/OverrideStandardRubricScore";
import {
  getScoreValueElementRatings,
  getIsBenchmarkAIGradingEnabled,
} from "ClassRoom/modules/utils";
import { getMYPIBDefinedGradeScalesQuery } from "GradingSetupV2/modules/GradingSetupV2Queries";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import { getIsConfigurationEnabledForCPAY } from "modules/PermissionModule";

const EMPTY_OBJ = {};

const styles = {
  learningGoalStyles: {
    criteriaSetHeaderLabelPreviewStyle: {
      minWidth: "auto",
    },
  },
  aiEvaluatedComment: {
    border: `1px dashed var(--interactive-tertiary)`,
    backgroundColor: `var(--decorative-background-subtle-violet)`,
    paddingBottom: "40px",
    cursor: "pointer",
  },
  commentTextArea: {
    lineHeight: "2rem",
    ...fontStyle.medium,
    fontSize: "1.4rem",
    paddingBottom: 16,
  },
  resetMargin: {
    margin: "0px",
  },
  starsTwoFilledIconStyle: {
    color: "var(--interactive-tertiary)",
  },
};

const navigationKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];

const Comment = props => {
  const {
    t,
    elementRemarks = [],
    updateComment,
    showCommentHeading,
    extraParams = {},
    updateEvaluationData,
    getInputRef,
    canPerformActionsInAssignment,
    isAiEvaluationEnabled,
    onAcceptAIEvaluation,
    assessmentId,
    parentId,
    showUpdatedDesignForAIGrading,
    isAIGeneratingResponseForTool,
    isEvaluationEnable,
    shouldStopKeyboardPropagation = false, // * We found an issue: when the ImageViewer and Comment components are rendered on the same page and the user tries to navigate inside the comment box using arrow keys, it does not work. Instead, the image navigates (next/previous) or zooms in/out.
  } = props;

  const isBenchmarkAIGradingEnabled = getIsBenchmarkAIGradingEnabled();

  const [isGeneratingAIResponse, setIsGeneratingAIResponse] = useState(false);

  const textAreaInputRef = useRef(null);
  const isUserActionRef = useRef(false);

  // * Stop navigation keys from propagating to ImageViewer/react-viewer
  useEffect(() => {
    if (!shouldStopKeyboardPropagation) return;

    const handleKeyDown = e => {
      const textarea = textAreaInputRef.current;
      // * Check if our textarea (or its inner element) is the active element
      const isTextareaFocused =
        document.activeElement === textarea ||
        textarea?.contains(document.activeElement);

      if (!isTextareaFocused) return;

      if (_.includes(navigationKeys, e.key)) {
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [shouldStopKeyboardPropagation]);

  const staffComment = _.find(
    elementRemarks,
    ({ creatorType = STAFF_USER }) => creatorType === STAFF_USER
  );

  const aiComment = _.find(elementRemarks, {
    creatorType: "AI",
    ibPYPElementType: "STUDENT_ASSIGNMENT",
  });

  const nonDeletedAIComment = _.find(elementRemarks, {
    creatorType: "AI",
    ibPYPElementType: "STUDENT_ASSIGNMENT",
    isDeleted: false,
  });

  const isAICommentDeleted = _.get(aiComment, "isDeleted", false);

  const isEvaluatedByAI =
    !_.isEmpty(aiComment) &&
    (isAiEvaluationEnabled ? !isAICommentDeleted : _.isEmpty(staffComment));

  const comment =
    isAiEvaluationEnabled && nonDeletedAIComment
      ? nonDeletedAIComment
      : staffComment || nonDeletedAIComment;

  const commentMessage = _.get(comment, "message", "");

  const [localCommentValue, setLocalCommentValue] = useState(commentMessage);

  useEffect(() => {
    if (!_.isEqual(localCommentValue, commentMessage)) {
      setLocalCommentValue(commentMessage);
    }
  }, [elementRemarks]);

  const setInputRef = ref => {
    textAreaInputRef.current = ref;
    getInputRef?.(ref);
  };

  const onEndAIResponse = () => {
    setTimeout(() => {
      setIsGeneratingAIResponse(false);
    }, 1700); // as 1500 ms debounce is there for cache update
  };

  const containerClass = classNames({
    [classes.commentContainer]: true,
    [classes.commentContainerWithoutHeading]: !showCommentHeading,
  });

  const { toolStyles, commentPlaceholder, showUpdatedDesign = false } =
    extraParams || {};
  const { containerStyle, commentHeadingStyle } = toolStyles || {};

  const placeholder = _.isEmpty(commentPlaceholder)
    ? t("common:typeHere")
    : commentPlaceholder;

  const commentHeadingClasses = classNames({
    [classes.commentHeading]:
      !showUpdatedDesign && !showUpdatedDesignForAIGrading,
    [classes.commentHeading2]: showUpdatedDesign,
    ["text-label"]: showUpdatedDesign || showUpdatedDesignForAIGrading,
  });

  const commentHeadingContainerClass = classNames({
    [classes.commentHeadingContainer]: true,
    [classes.aiCommentHeadingContainer]: aiComment,
  });

  const commentStyles = useMemo(
    () => ({
      ...(nonDeletedAIComment || isGeneratingAIResponse
        ? styles.aiEvaluatedComment
        : {}),
      ...styles.commentTextArea,
    }),
    [nonDeletedAIComment, isGeneratingAIResponse]
  );

  const acceptAIEvaluation = ({ isAcceptAllForTool = false } = {}) => {
    onAcceptAIEvaluation({
      inputText: commentMessage,
      isDeleteAiRatings: true,
      isAcceptAllForTool,
    });
  };

  const onChangeTextAreaInput = e => {
    setLocalCommentValue(e.target.value);

    // If the comment is focused using keyboard and is still showing AI resposne
    // as soon as user starts typing, we need to accept the AI evaluation first
    if (isAiEvaluationEnabled && isEvaluatedByAI) {
      acceptAIEvaluation();
    } else {
      updateComment({ value: e.target.value });
    }
  };

  const handleTextAreaFocus = () => {
    updateEvaluationData({ isTextFocus: true });

    if (isAiEvaluationEnabled && isUserActionRef.current && isEvaluatedByAI) {
      acceptAIEvaluation();
    }
    isUserActionRef.current = false;
  };

  const renderSkeletonLoader = () => {
    const totalSkeletons = 4;

    return (
      <div className={classes.skeletonContainer}>
        {_.times(totalSkeletons, index => {
          return (
            <div key={index}>
              <SkeletonLoader width={"100%"} height={"14px"} />
            </div>
          );
        })}
        <SkeletonLoader width={"80%"} height={"15px"} />
      </div>
    );
  };

  return (
    <div className={containerClass} style={containerStyle}>
      {showCommentHeading && (
        <div className={commentHeadingContainerClass}>
          <div
            className={commentHeadingClasses}
            style={!aiComment ? commentHeadingStyle : {}}
          >
            {t("common:comment")}
          </div>

          {isAiEvaluationEnabled &&
          isEvaluationEnable &&
          aiComment &&
          !isAIGeneratingResponseForTool &&
          !isBenchmarkAIGradingEnabled ? (
            <div className={classes.inlineEvaluationIconContainer}>
              {isEvaluatedByAI && !isGeneratingAIResponse ? (
                <Tooltip
                  tooltip={t("ai:approve_ai_evaluation", {
                    label: t("common:comment"),
                  })}
                  dsVersion="2.0"
                >
                  <IconButton
                    icon={<TickCircleOpenOutlined />}
                    dsVersion="2.0"
                    variant="neutral"
                    type="plain"
                    size="medium"
                    disabled={!isEvaluatedByAI}
                    onClick={() =>
                      acceptAIEvaluation({ isAcceptAllForTool: true })
                    }
                  />
                </Tooltip>
              ) : null}
              <Dropdown
                overlay={
                  <AIGradingPromptBox
                    promptType="TEXT_TOOL_EVALUATION"
                    sourceText={localCommentValue}
                    assessmentToolType={"COMMENT"}
                    entityId={parentId}
                    assessmentId={assessmentId}
                    streamingActionText={t("ai:writing")}
                    updatedSourceText={({ value }) => {
                      setIsGeneratingAIResponse(true);
                      setLocalCommentValue(value);
                      updateComment({ value, creatorType: "AI" });
                    }}
                    onEndAIResponse={onEndAIResponse}
                  />
                }
              >
                <Tooltip tooltip={t("ai:regenerate")} dsVersion="2.0">
                  <IconButton
                    icon={
                      <StarsTwoFilled style={styles.starsTwoFilledIconStyle} />
                    }
                    dsVersion="2.0"
                    variant="neutral"
                    type="plain"
                    size="medium"
                  />
                </Tooltip>
              </Dropdown>
            </div>
          ) : null}
        </div>
      )}
      {isAIGeneratingResponseForTool ? (
        <div className={classes.skeletonLoaderContainer}>
          {renderSkeletonLoader()}
        </div>
      ) : (
        <div className={classes.commentInputContainer}>
          <TextAreaInput
            ref={setInputRef}
            className={classes.commentTextInput}
            placeholder={!isGeneratingAIResponse && placeholder}
            onChange={onChangeTextAreaInput}
            value={localCommentValue}
            disabled={!isEvaluationEnable || !canPerformActionsInAssignment}
            onMouseDown={() => (isUserActionRef.current = true)}
            onFocus={handleTextAreaFocus}
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
            onBlur={() => updateEvaluationData({ isTextFocus: false })}
            style={commentStyles}
            testId={`assessment-evaluation-toolresponse-comment-input`}
          />
        </div>
      )}
    </div>
  );
};

class ToolResponse extends React.PureComponent {
  constructor(props) {
    super(props);

    this.debouncedHandleAssessmentToolResponseInCache = _.debounce(
      props.handleAssessmentToolResponseInCache,
      250
    );
  }

  updateEvaluationCell = ({
    rowId,
    columnId,
    subjectiveResponse,
    type,
    assessmentToolId,
    isDebounce = false,
    creatorType,
    isDeleteAiRatings,
  }) => {
    const {
      updateField,
      handleAssessmentToolResponseInCache,
      parentId,
      userType,
      parentType,
      toolType,
      projectId,
      studentId,
      extraParams,
      userEntityType,
      toolResponsesDataLocal,
      setToolResponsesDataLocal,
      isAutoSaveDisabled,
    } = this.props;

    // parent is not allowed to update rubric cell
    if (userType === "parent") {
      return;
    }

    let params = {
      parentId,
      parentType,
      projectId,
      studentId,
      rowId,
      type,
      columnId,
      isDeleteAiRatings,
      subjectiveResponse,
      ..._.pick(extraParams, [
        "assignmentId",
        "sourceFrom",
        "courseId",
        "queryVars",
        "projectGroupType",
      ]),
      toolResponsesDataLocal,
      setToolResponsesDataLocal,
      creatorType,
    };

    if (assessmentToolId) {
      params = {
        ...params,
        assessmentToolId,
        isToolResponsesV2: true,
      };
    }

    if (_.isFunction(setToolResponsesDataLocal)) {
      const updatedToolResponses = getUpdatedToolResponses({
        ...params,
        userEntityType: creatorType || userEntityType,
        toolResponsesData: _.get(toolResponsesDataLocal, parentId, []),
      });

      setToolResponsesDataLocal({
        ...toolResponsesDataLocal,
        [parentId]: updatedToolResponses,
      });

      if (isAutoSaveDisabled) return;

      params = {
        ...params,
        toolResponsesDataLocal: updatedToolResponses,
      };
    } else {
      if (isDebounce) {
        this.debouncedHandleAssessmentToolResponseInCache(params);
      } else {
        handleAssessmentToolResponseInCache(params);
      }
    }

    updateField({
      key: `${rowId}_${columnId}`,
      params,
      type: "EVALUATION_TOOL",
      isDebounce: toolType == "SINGLE_POINT_RUBRIC",
    });
  };

  renderComment = () => {
    const {
      t,
      elementRemarks,
      updateComment,
      isEvaluated,
      showCommentHeading,
      extraParams = {},
      getInputRef,
      onAcceptAIEvaluation,
      canPerformActionsInAssignment = true,
      isAiEvaluationEnabled,
      assessmentId,
      parentId,
      isAIGeneratingResponseForTool,
      showUpdatedDesignForAIGrading,
      isEvaluationEnable,
      shouldStopKeyboardPropagation,
    } = this.props;

    return (
      <Comment
        t={t}
        elementRemarks={elementRemarks}
        updateComment={updateComment}
        showCommentHeading={showCommentHeading}
        extraParams={extraParams}
        updateEvaluationData={updateEvaluationData}
        getInputRef={getInputRef}
        assessmentId={assessmentId}
        parentId={parentId}
        onAcceptAIEvaluation={onAcceptAIEvaluation}
        isAiEvaluationEnabled={isAiEvaluationEnabled}
        isAIGeneratingResponseForTool={isAIGeneratingResponseForTool}
        canPerformActionsInAssignment={canPerformActionsInAssignment}
        showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
        isEvaluationEnable={isEvaluationEnable}
        shouldStopKeyboardPropagation={shouldStopKeyboardPropagation}
      />
    );
  };

  getToolResponses = () => {
    const {
      parentId,
      assessmentToolId,
      toolResponses,
      toolResponsesDataLocal,
    } = this.props;

    if (!_.isEmpty(toolResponsesDataLocal)) {
      return _.get(
        _.find(
          _.get(toolResponsesDataLocal, parentId, []),
          ({ toolId }) => toolId === assessmentToolId
        ),
        "toolResponses",
        {}
      );
    }

    return toolResponses;
  };

  getIBPypElementRatings = () => {
    const {
      ibPYPElementRatings,
      ibPYPElementRatingsLocal,
      parentId,
    } = this.props;

    if (!_.isEmpty(ibPYPElementRatingsLocal)) {
      return _.get(ibPYPElementRatingsLocal, parentId, []);
    }

    return ibPYPElementRatings;
  };

  renderEvaluationStatusRight = () => {
    const {
      parentId,
      toolType,
      assessmentTool,
      isEvaluationEnable,
      student,
      extraParams,
      academicTermId,
      sourceFrom,
      setIBPYPElementRatingsLocal,
      isAutoSaveDisabled,
      isEvaluated,
      onAcceptAIEvaluation,
      isAiEvaluationEnabled,
      assessmentId,
      studentId,
      isAIGeneratingResponseForTool,
      showDescriptionToggle,
      showOverrideScore,
    } = this.props;

    const toolResponses = this.getToolResponses();
    const ibPYPElementRatings = this.getIBPypElementRatings();

    const { updateScoreLocal, studentDetails, assessmentTools } =
      extraParams || {};

    const scoreValue = getScoreValueElementRatings({
      ibPYPElementRatings,
      isAiEvaluationEnabled,
    });

    const aiSelectedScore = _.find(ibPYPElementRatings, {
      creatorType: "AI",
      isDeleted: false,
    });

    const isAIEvaluationExist =
      _.some(toolResponses, response => {
        return response?.creatorType === "AI";
      }) ||
      _.some(ibPYPElementRatings, rating => {
        return rating?.creatorType === "AI";
      });

    const showAIActions =
      isAiEvaluationEnabled && !isEvaluated && isAIEvaluationExist;

    const isScoreOrStandardBasedRubric = _.includes(
      [SCORE_BASED_RUBRIC, STANDARD_BASED_RUBRIC],
      toolType
    );

    const showOverrideStandardRubricScore = [
      SCORE_BASED_RUBRIC,
      STANDARD_BASED_RUBRIC,
    ].includes(toolType);

    const showAIActionsComponent =
      showAIActions &&
      !isScoreOrStandardBasedRubric &&
      !isAIGeneratingResponseForTool;

    if (!showOverrideStandardRubricScore && !showAIActionsComponent) {
      return null;
    }

    return (
      <div className={classes.overrideScoreContainer}>
        {showOverrideStandardRubricScore ? (
          <OverrideStandardRubricScore
            scoreValue={scoreValue}
            aiSelectedScore={aiSelectedScore}
            isAISelectedScore={!_.isEmpty(aiSelectedScore)}
            assessmentTool={assessmentTool}
            isEvaluationEnable={isEvaluationEnable}
            studentAssignmentId={parentId}
            studentDetails={studentDetails || student}
            academicTermId={academicTermId}
            sourceFrom={sourceFrom}
            toolResponses={toolResponses}
            setIBPYPElementRatingsLocal={setIBPYPElementRatingsLocal}
            isAutoSaveDisabled={isAutoSaveDisabled}
            updateScoreLocal={updateScoreLocal}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            type={toolType}
            isEvaluated={isEvaluated}
            ibPYPElementRatings={ibPYPElementRatings}
            assessmentId={assessmentId}
            studentId={studentId}
            assignmentId={extraParams?.assignmentId}
            assessmentTools={assessmentTools}
            showDescriptionToggle={showDescriptionToggle}
            showOverrideScore={showOverrideScore}
          />
        ) : null}

        {showAIActionsComponent ? (
          <AIGradingActions
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            academicTermId={academicTermId}
            assessmentToolType={toolType}
            toolResponses={toolResponses}
            ibPYPElementRatings={ibPYPElementRatings}
          />
        ) : null}
      </div>
    );
  };

  render() {
    const {
      t,
      toolType,
      assessmentTool,
      isEvaluationEnable,
      isEvaluatedByStudent,
      showEvaluateStatus,
      rubricInnerConStyle,
      rubricFirstColumnCellStyle,
      mypObjectiveRubricOptions,
      parentId,
      parentType,
      studentId,
      isEvaluated,
      ratingElementSubType,
      studentIds = [],
      containerStyle,
      isEvaluatedByTeacher,
      rubricRatingContainerStyle,
      rubricRatingCompContainerStyle,
      rubricRatingTextContainerStyle,
      showRatingContainerInViewMode,
      isCommentEnabled,
      shouldTakeFullWidth,
      assessmentToolId,
      typeOfScale,
      isSharedWithStudent,
      extraParams,
      userType,
      rubricHeaderRowCellStyle,
      isSplitView = false,
      evaluationStatusIconSize,
      assessmentFields,
      curriculumType,
      course,
      student,
      isLearningGoalsEvaluationComplete,
      projectId,
      academicTermId = null,
      ibPYPElementRatingsLocal,
      setIBPYPElementRatingsLocal,
      criteriaRatingElementsContainerStyle,
      criteriaRatingCustomRatingElementClass,
      showCommentForRating,
      ibPYPElementRemarks,
      onUpdateCriteriaComment,
      elementRemarks,
      commentInputRef,
      activeComment,
      sourceFrom,
      isAutoSaveDisabled,
      evaluationStatusExtraParams = {},
      handleRefetchStudentRatings,
      showEmptyValueInSelection,
      getInputRef,
      isSelectedClickDisabled,
      isAiEvaluationEnabled,
      onAcceptAIEvaluation,
      assessmentId,
      collapseJustification,
      showUpdatedDesignForAIGrading,
      isAIGeneratingResponseForTool,
      acceptAIEvaluationForLearningGoals,
      isEvaluationModalView = false,
      learningCourseId,
      isUsingNewGradingSetupInCourses,
    } = this.props;

    const toolResponses = this.getToolResponses();
    const ibPYPElementRatings = this.getIBPypElementRatings();

    const isAIEvaluatedScore = _.find(ibPYPElementRatings, {
      creatorType: "AI",
      isDeleted: false,
      ibPYPElementType: "SCORE",
    });

    let toolResponseComponent = null;
    switch (toolType) {
      case "RUBRIC":
        toolResponseComponent = (
          <RubricTable
            mode={"view"}
            isEvaluation={isEvaluationEnable}
            isEvaluationEnable={isEvaluationEnable}
            updateEvaluationCell={params =>
              this.updateEvaluationCell({
                ...params,
                assessmentToolId,
                type: "RUBRIC",
              })
            }
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            rubric={assessmentTool}
            toolEvaluationResponses={toolResponses}
            isEvaluatedByStudent={isEvaluatedByStudent}
            innerConStyle={rubricInnerConStyle}
            firstColumnCellStyle={rubricFirstColumnCellStyle}
            shouldTakeFullWidth={shouldTakeFullWidth}
            isSplitView={isSplitView}
            isEvaluated={isEvaluated}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            {..._.pick(extraParams, ["showUpdatedDesign"])}
          />
        );
        break;
      case "SINGLE_POINT_RUBRIC":
        toolResponseComponent = (
          <SinglePointRubricTable
            mode={"view"}
            parentId={parentId}
            assessmentId={assessmentId}
            isEvaluation={true}
            isEvaluationEnable={isEvaluationEnable}
            updateEvaluationCell={params =>
              this.updateEvaluationCell({
                ...params,
                assessmentToolId,

                type: "SINGLE_POINT_RUBRIC",
              })
            }
            rubric={assessmentTool}
            toolEvaluationResponses={toolResponses}
            isEvaluated={isEvaluated}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            isEvaluatedByStudent={isEvaluatedByStudent}
            isSplitView={isSplitView}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            {..._.pick(extraParams, ["showUpdatedDesign"])}
          />
        );
        break;
      case "CHECKLIST":
        toolResponseComponent = (
          <ChecklistTable
            checklist={assessmentTool}
            mode={"view"}
            isEvaluation={isEvaluationEnable}
            isEvaluationEnable={isEvaluationEnable}
            updateEvaluationCell={params =>
              this.updateEvaluationCell({
                ...params,
                assessmentToolId,
                type: "CHECKLIST",
              })
            }
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            toolEvaluationResponses={toolResponses}
            isEvaluatedByStudent={isEvaluatedByStudent}
            isSplitView={isSplitView}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            {...extraParams}
          />
        );
        break;
      case "MYP_OBJECTIVE_RUBRIC":
      case "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC":
        toolResponseComponent = (
          <MYPObjectiveRubric
            ratingElementSubType={ratingElementSubType}
            assessmentTool={assessmentTool}
            mode={"view"}
            isEvaluationEnable={isEvaluationEnable}
            parentId={parentId}
            parentType={parentType}
            projectId={projectId}
            studentId={studentId}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            studentIds={studentIds}
            isEvaluated={isEvaluated}
            ibPYPElementRatings={ibPYPElementRatings}
            options={mypObjectiveRubricOptions}
            updateEvaluationCell={params =>
              this.updateEvaluationCell({
                ...params,
                assessmentToolId,
                type: toolType,
              })
            }
            toolEvaluationResponses={toolResponses}
            isEvaluatedByStudent={isEvaluatedByStudent}
            rubricRatingContainerStyle={rubricRatingContainerStyle}
            rubricRatingCompContainerStyle={rubricRatingCompContainerStyle}
            rubricRatingTextContainerStyle={rubricRatingTextContainerStyle}
            rubricHeaderRowCellStyle={rubricHeaderRowCellStyle}
            criteriaRatingElementsContainerStyle={
              criteriaRatingElementsContainerStyle
            }
            criteriaRatingCustomRatingElementClass={
              criteriaRatingCustomRatingElementClass
            }
            showRatingContainerInViewMode={showRatingContainerInViewMode}
            isSplitView={isSplitView}
            toolType={toolType}
            ibPYPElementRatingsLocal={ibPYPElementRatingsLocal}
            setIBPYPElementRatingsLocal={setIBPYPElementRatingsLocal}
            academicTermId={academicTermId}
            extraParams={extraParams}
            showCommentForRating={showCommentForRating}
            ibPYPElementRemarks={ibPYPElementRemarks}
            onUpdateCriteriaComment={onUpdateCriteriaComment}
            commentInputRef={commentInputRef}
            activeComment={activeComment}
            isAutoSaveDisabled={isAutoSaveDisabled}
            {..._.pick(extraParams, ["sourceFrom", "showUpdatedDesign"])}
            showEmptyValueInSelection={showEmptyValueInSelection}
            isSelectedClickDisabled={isSelectedClickDisabled}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
          />
        );
        break;
      case "DP_ASSESSMENT_RUBRIC":
        toolResponseComponent = (
          <CriteriaRubricAssessmentWrapper
            parentId={parentId}
            assessmentId={assessmentId}
            parentType={parentType}
            criteria={assessmentTool?.criteria}
            typeOfScale={typeOfScale}
            studentId={studentId}
            studentIds={studentIds}
            isEvaluationEnable={isEvaluationEnable}
            ibPYPElementRatings={ibPYPElementRatings}
            isEvaluated={isEvaluated}
            isSharedWithStudent={isSharedWithStudent}
            assessmentTool={assessmentTool}
            isSplitView={isSplitView}
            assignmentId={extraParams?.assignmentId}
            isEvaluation={isEvaluationEnable}
            ibPYPElementRatingsLocal={ibPYPElementRatingsLocal}
            setIBPYPElementRatingsLocal={setIBPYPElementRatingsLocal}
            academicTermId={academicTermId}
            sourceFrom={sourceFrom}
            isAutoSaveDisabled={isAutoSaveDisabled}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            assessmentToolId={assessmentToolId}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            assessmentFields={assessmentFields}
            customSelectedRatingElement={classes.customSelectedRatingElement}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            isWorksheetScorebook={
              _.get(extraParams, "sourceFrom", null) === "SCOREBOOK"
            }
            {..._.pick(extraParams, ["showUpdatedDesign"])}
          />
        );
        break;
      case "COMMENT":
        toolResponseComponent = (
          <React.Fragment>{this.renderComment()}</React.Fragment>
        );
        break;
      case "SCORE":
        toolResponseComponent = (
          <ScoreAssessmentTool
            isEvaluated={isEvaluated}
            isSplitView={isSplitView}
            isEvaluationEnable={isEvaluationEnable}
            academicTermId={academicTermId}
            handleRefetchStudentRatings={handleRefetchStudentRatings}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            getInputRef={getInputRef}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            collapseJustification={collapseJustification}
            assessmentId={assessmentId}
            {...extraParams}
          />
        );
        break;
      case ASSESSMENT_GRADE_BOUNDARY:
        toolResponseComponent = (
          <AssessmentIBScaleTool
            parentId={parentId}
            parentType={parentType}
            isEvaluationEnable={isEvaluationEnable}
            assessmentTool={assessmentTool}
            assessmentToolType={toolType}
            isEvaluated={isEvaluated}
            studentId={studentId}
            assignmentId={extraParams?.assignmentId}
            studentIds={studentIds}
            ibPYPElementRatings={ibPYPElementRatings}
            academicTermId={academicTermId}
            ibPYPElementRatingsLocal={ibPYPElementRatingsLocal}
            setIBPYPElementRatingsLocal={setIBPYPElementRatingsLocal}
            isAutoSaveDisabled={isAutoSaveDisabled}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            assessmentId={assessmentId}
            customSelectedRatingElement={classes.customSelectedRatingElement}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            collapseJustification={collapseJustification}
            assessmentFields={assessmentFields}
            {...extraParams}
          />
        );
        break;
      case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
      case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY: {
        const IBScaleTool =
          isUsingNewGradingSetupInCourses &&
          getIsIBGradeScaleV2ForNewGradingSetupEnabled()
            ? AutomaticIBScaleToolV2
            : AutomaticIBScaleTool;
        toolResponseComponent = (
          <IBScaleTool
            mode={"view"}
            assessmentTool={assessmentTool}
            parentId={parentId}
            parentType={parentType}
            isEvaluationEnable={isEvaluationEnable}
            isEvaluated={isEvaluated}
            studentId={studentId}
            studentIds={studentIds}
            ibPYPElementRatings={ibPYPElementRatings}
            isSplitView={isSplitView}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            showUpdatedDesignForAIGrading={showUpdatedDesignForAIGrading}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            customSelectedRatingElement={
              isAIEvaluatedScore
                ? classes.customAISelectedRatingElement
                : classes.customSelectedRatingElement
            }
            {...extraParams}
          />
        );
        break;
      }
      case "LEARNING_GOALS":
        return (
          <PypEvaluationV2
            isComplete={isLearningGoalsEvaluationComplete}
            parentId={parentId}
            parentType={"STUDENT_ASSIGNMENT"}
            showToggleStudentPypEvaluateStatus={false}
            evaluateIBPYPElements={true}
            studentDetails={student}
            assessmentFields={assessmentFields}
            ibPYPElementRatings={ibPYPElementRatings}
            isEvaluated={isEvaluated}
            curriculumProgramType={curriculumType}
            course={course}
            isMinimalDesign={true}
            showCriteriaDescriptorButton={true}
            showCriteriaColumns={true}
            closePopoverOnRating={true}
            infoIconSize={"xxx-small"}
            sourceFrom={sourceFrom}
            showCriteriaSetHeader={true}
            shouldCloseOnValueChange={true}
            showDropdown={true}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            academicTermId={academicTermId}
            {...styles.learningGoalStyles}
            ibPYPElementRatingsLocal={ibPYPElementRatingsLocal}
            setIBPYPElementRatingsLocal={setIBPYPElementRatingsLocal}
            isAutoSaveDisabled={isAutoSaveDisabled}
            onAcceptAIEvaluation={acceptAIEvaluationForLearningGoals}
            elementRemarks={elementRemarks}
            isEvaluationModalView={isEvaluationModalView}
            learningCourseId={learningCourseId}
            {..._.pick(extraParams, ["showUpdatedDesign"])}
          />
        );
      case "AI_ANALYSIS": {
        const { toolStyles } = extraParams || {};
        const { containerStyle, commentHeadingStyle, commentStyle } =
          toolStyles || {};

        const analysis = _.find(
          elementRemarks,
          ({ creatorType }) => creatorType === "AI"
        );

        const analysisMessage = _.get(analysis, "message", "");

        return (
          <div className={classes.analysisContainer} style={containerStyle}>
            <div className={classes.commentHeading} style={commentHeadingStyle}>
              {t("common:analysis")}
            </div>
            <div className={classes.analysis} style={commentStyle}>
              {!_.isEmpty(analysisMessage)
                ? analysisMessage
                : t("classRoom:no_analysis_to_display_msg")}
            </div>
          </div>
        );
      }
      case SCORE_BASED_RUBRIC:
      case STANDARD_BASED_RUBRIC: {
        const {
          assessmentTool,
          handleIBPYPElementRatingsForStandardRubric,
        } = this.props;

        const { assessmentTools } = extraParams || {};

        const updateEvaluationCell = params => {
          const { rowId, columnId } = params || {};

          this.updateEvaluationCell({
            ...params,
            assessmentToolId,
            type: toolType,
          });

          handleIBPYPElementRatingsForStandardRubric({
            assessmentTool,
            rowId,
            columnId,
            toolResponses,
            isAutoSaveDisabled,
            parentId,
            parentType,
            academicTermId,
            sourceFrom,
            ibPYPElementRatings,
            ibPYPElementRatingsLocal,
            setIBPYPElementRatingsLocal,
            studentIds,
            studentId,
            isEvaluated,
            toolType,
            assessmentTools,
            learningCourseId,
          });
        };

        toolResponseComponent = (
          <StandardRubric
            mode={"view"}
            isEvaluationEnable={isEvaluationEnable}
            updateEvaluationCell={updateEvaluationCell}
            assessmentTool={assessmentTool}
            toolEvaluationResponses={toolResponses}
            isEvaluatedByStudent={isEvaluatedByStudent}
            isSplitView={isSplitView}
            sourceFrom={sourceFrom}
            onAcceptAIEvaluation={onAcceptAIEvaluation}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isAIGeneratingResponseForTool}
            type={toolType}
            assessmentTools={assessmentTools}
          />
        );

        break;
      }
      case GRADE_SCALE_TOOL: {
        const {
          updateScoreLocal,
          studentDetails,
          assessmentTools,
          toolResponsesData,
        } = extraParams || {};

        toolResponseComponent = (
          <AssessmentGradeScaleTool
            mode={"view"}
            isEvaluationEnable={isEvaluationEnable}
            assessmentTool={assessmentTool}
            isSplitView={isSplitView}
            sourceFrom={sourceFrom}
            studentDetails={studentDetails || student}
            parentId={parentId}
            academicTermId={academicTermId}
            updateScoreLocal={updateScoreLocal}
            isAutoSaveDisabled={isAutoSaveDisabled}
            ibPYPElementRatingsLocal={ibPYPElementRatingsLocal}
            setIBPYPElementRatingsLocal={setIBPYPElementRatingsLocal}
            ibPYPElementRatings={ibPYPElementRatings}
            isEvaluated={isEvaluated}
            assessmentTools={assessmentTools}
            toolResponsesData={toolResponsesData}
            handleRefetchStudentRatings={handleRefetchStudentRatings}
          />
        );
        break;
      }
    }

    const evaluationStatusRightComp = this.renderEvaluationStatusRight();

    // if right component exists then show evaluation status on the left and add margin bottom
    const evaluationStatusContainerClass = classNames({
      [classes.evaluationStatusContainer]: evaluationStatusRightComp,
    });

    // margin will be handled through classes.evaluationStatusContainer if right component exists
    const evaluationInstructionContainerStyle = evaluationStatusRightComp
      ? styles.resetMargin
      : EMPTY_OBJ;

    return !!toolResponseComponent || isCommentEnabled ? (
      <div className={classes.container} style={containerStyle}>
        {showEvaluateStatus ? (
          <div className={evaluationStatusContainerClass}>
            <EvaluationStatus
              toolType={toolType}
              isEvaluatedByStudent={isEvaluatedByStudent}
              isEvaluatedByTeacher={isEvaluatedByTeacher}
              userType={userType}
              evaluationStatusIconSize={evaluationStatusIconSize}
              isSplitView={isSplitView}
              evaluationInstructionContainerStyle={
                evaluationInstructionContainerStyle
              }
              {...evaluationStatusExtraParams}
            />
            {evaluationStatusRightComp}
          </div>
        ) : null}
        {toolResponseComponent}
        {isCommentEnabled && this.renderComment()}
      </div>
    ) : null;
  }
}

const mapActionCreators = {
  handleAssessmentToolResponse,
  updateField,
  handleAssessmentToolResponseInCache,
  updateEvaluationData,
  updateScoreAssessmentTool,
  handleIBPYPElementRatingsForStandardRubric,
  handleIBPYPElementRatingsForGenericRubric,
};

const mapStateToProps = (state, ownProps) => {
  const { parentId, assessmentFields } = ownProps;
  const isAIGeneratingResponseForTool = getIsAIGeneratingResponseForTool({
    state,
    studentAssignmentId: parentId,
    assessmentFields,
  });
  const curriculumType = getCurrentCurriculumProgramType(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const academicYearId = getSelectedAcademicYearId(state);
  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId,
    academicYearId,
  });

  return {
    isData: true,
    isLoading: false,
    userType: _.get(state, "login.userInfo.user_type", ""),
    userEntityType: _.get(state, "login.userInfo.userEntityType", ""),
    isAIGeneratingResponseForTool,
    curriculumType,
    curriculumProgramId,
    isUsingNewGradingSetupInCourses,
  };
};

ToolResponse.propTypes = {
  showEvaluateStatus: PropTypes.bool,
  shouldTakeFullWidth: PropTypes.bool,
  showCommentHeading: PropTypes.bool,
};

ToolResponse.defaultProps = {
  showEvaluateStatus: true,
  isEvaluatedByTeacher: true,
  isCommentEnabled: false,
  shouldTakeFullWidth: false,
  showCommentHeading: true,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getMYPIBDefinedGradeScalesQuery, {
    name: "getGradeScaleDetails",
    skip: ({ curriculumType, isUsingNewGradingSetupInCourses }) =>
      curriculumType !== CURRICULUM_TYPE_MYP ||
      !isUsingNewGradingSetupInCourses,
    options: ({ curriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: curriculumProgramId,
        filters: {
          isArchived: false,
          types: MYP_IB_DEFINED,
          entityType: "CURRICULUM_PROGRAM",
          entityDetailFilters: [
            {
              curriculumProgramId,
            },
          ],
        },
        gradeScaleEntityDetailsFilters: {
          entityType: "CURRICULUM_PROGRAM",
          entityDetails: [
            {
              curriculumProgramId,
            },
          ],
        },
      },
    }),
  })
)(ToolResponse);
